import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Heart, BookOpen, Play, Clock, CheckCircle, Users, User, Plus,
  Search, ChevronRight, ChevronDown, Eye, FileText, Link as LinkIcon,
  Sparkles, GraduationCap, AlertCircle
} from 'lucide-react';
import { getPlainTextPreview, formatDateSafe } from '../utils/textUtils';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelfCare = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tasks'); // 'tasks' or 'explore'
  const [activeSection, setActiveSection] = useState('assigned_to_me');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTask, setExpandedTask] = useState(null);
  
  // Task data
  const [taskData, setTaskData] = useState({
    assigned_to_me: [],
    assigned_by_me: [],
    completed: [],
    counts: { assigned_to_me: 0, assigned_by_me: 0, completed: 0 }
  });
  
  // Explore resources data (educator content)
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tasks/by-dimension/self_care`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskData(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      setLoadingPrograms(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/programs?pillar=wellness`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrograms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchPrograms();
  }, [fetchTasks, fetchPrograms]);

  const getPriorityColor = (priority) => {
    if (priority >= 8) return '#DC2626';
    if (priority >= 5) return '#D97706';
    return '#059669';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed': return { bg: 'rgba(76, 175, 80, 0.1)', color: '#4CAF50' };
      case 'in_progress': return { bg: 'rgba(33, 150, 243, 0.1)', color: '#2196F3' };
      default: return { bg: 'rgba(255, 152, 0, 0.1)', color: '#FF9800' };
    }
  };

  const cleanName = (name) => {
    if (!name) return 'Unknown';
    return name.replace(/\s+\d+$/, '').trim();
  };

  const filterTasks = (tasks) => {
    if (!searchQuery) return tasks;
    const query = searchQuery.toLowerCase();
    return tasks.filter(t =>
      t.title?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query)
    );
  };

  const renderTaskCard = (task) => {
    const isExpanded = expandedTask === task.id;
    const statusStyle = getStatusStyle(task.status);

    return (
      <div
        key={task.id}
        className="card"
        style={{
          padding: '16px',
          marginBottom: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          borderLeft: `4px solid #4CAF50`
        }}
        onClick={() => setExpandedTask(isExpanded ? null : task.id)}
        data-testid={`task-card-${task.id}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {task.title}
              </h4>
              <span style={{
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: '600',
                background: `${getPriorityColor(task.priority)}15`,
                color: getPriorityColor(task.priority)
              }}>
                P{task.priority}
              </span>
            </div>
            <p style={{
              margin: '0 0 8px 0',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              display: '-webkit-box',
              WebkitLineClamp: isExpanded ? 'none' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: isExpanded ? 'visible' : 'hidden'
            }}>
              {getPlainTextPreview(task.description, isExpanded ? 500 : 100)}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '600',
              background: statusStyle.bg,
              color: statusStyle.color,
              textTransform: 'capitalize'
            }}>
              {task.status?.replace('_', ' ')}
            </span>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </div>
        </div>

        {isExpanded && (
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <User size={14} />
                <span>Assigned by: {cleanName(task.assigned_by_user?.full_name)}</span>
              </div>
              {task.deadline && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  <Clock size={14} />
                  <span>Due: {formatDateSafe(task.deadline)}</span>
                </div>
              )}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dashboard/tasks/${task.id}`);
              }}
              style={{
                padding: '8px 16px',
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Eye size={14} /> View Details
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderProgramCard = (program) => {
    const totalSessions = program.sessions_count || 0;
    
    return (
      <div
        key={program.id}
        className="card"
        style={{
          padding: '20px',
          cursor: 'pointer',
          transition: 'all 0.2s',
          borderLeft: '4px solid #10B981'
        }}
        onClick={() => navigate(`/dashboard/programs/${program.id}`)}
        data-testid={`program-card-${program.id}`}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <GraduationCap size={18} style={{ color: '#10B981' }} />
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                {program.title}
              </h4>
            </div>
            <p style={{
              margin: 0,
              fontSize: '13px',
              color: 'var(--text-secondary)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {program.description}
            </p>
          </div>
          {program.status === 'published' && (
            <span style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              background: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981'
            }}>
              Available
            </span>
          )}
        </div>

        {/* Stats */}
        <div style={{ 
          display: 'flex', 
          gap: '16px', 
          padding: '12px', 
          background: 'var(--bg-secondary)', 
          borderRadius: '8px',
          marginBottom: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <Play size={14} style={{ color: '#10B981' }} />
            <span>{totalSessions} Sessions</span>
          </div>
          {program.views > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <Eye size={14} />
              <span>{program.views} views</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {program.tags && program.tags.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {program.tags.map((tag, i) => (
              <span
                key={i}
                style={{
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366F1'
                }}
              >
                {tag.name || tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: 'rgba(76, 175, 80, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Heart size={24} style={{ color: '#4CAF50' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Self Care
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
              Wellness tasks and resources for personal well-being
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/tasks/new?dimension=self_care')}
          className="gradient-btn"
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <Plus size={18} /> New Task
        </button>
      </div>

      {/* Main Tabs - Tasks vs Explore */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '24px',
        background: 'var(--bg-secondary)',
        padding: '4px',
        borderRadius: '12px',
        width: 'fit-content'
      }}>
        <button
          onClick={() => setActiveTab('tasks')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            background: activeTab === 'tasks' ? '#4CAF50' : 'transparent',
            color: activeTab === 'tasks' ? 'white' : 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          data-testid="tab-tasks"
        >
          <CheckCircle size={16} />
          My Tasks
        </button>
        <button
          onClick={() => setActiveTab('explore')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            background: activeTab === 'explore' ? '#10B981' : 'transparent',
            color: activeTab === 'explore' ? 'white' : 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          data-testid="tab-explore"
        >
          <Sparkles size={16} />
          Explore Resources ({programs.filter(p => p.status === 'published').length})
        </button>
      </div>

      {/* Tasks Tab Content */}
      {activeTab === 'tasks' && (
        <>
          {/* Task Section Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid var(--border-color)',
            marginBottom: '20px',
            gap: '8px'
          }}>
            <button
              onClick={() => setActiveSection('assigned_to_me')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: activeSection === 'assigned_to_me' ? '#4CAF50' : 'var(--text-tertiary)',
                borderBottom: activeSection === 'assigned_to_me' ? '3px solid #4CAF50' : '3px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="section-assigned-to-me"
            >
              <User size={16} />
              Assigned to Me ({taskData.counts.assigned_to_me})
            </button>
            <button
              onClick={() => setActiveSection('assigned_by_me')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: activeSection === 'assigned_by_me' ? '#3B82F6' : 'var(--text-tertiary)',
                borderBottom: activeSection === 'assigned_by_me' ? '3px solid #3B82F6' : '3px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="section-assigned-by-me"
            >
              <Users size={16} />
              Assigned by Me ({taskData.counts.assigned_by_me})
            </button>
            <button
              onClick={() => setActiveSection('completed')}
              style={{
                padding: '12px 20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: activeSection === 'completed' ? '#059669' : 'var(--text-tertiary)',
                borderBottom: activeSection === 'completed' ? '3px solid #059669' : '3px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="section-completed"
            >
              <CheckCircle size={16} />
              Completed ({taskData.counts.completed})
            </button>
          </div>

          {/* Search */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)'
              }} />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          </div>

          {/* Task List */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div>
              {filterTasks(taskData[activeSection]).length === 0 ? (
                <div className="card" style={{ padding: '60px', textAlign: 'center' }}>
                  <AlertCircle size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No tasks found</h3>
                  <p style={{ margin: '0 0 20px 0', color: 'var(--text-tertiary)' }}>
                    {activeSection === 'assigned_to_me' && 'No self-care tasks assigned to you yet'}
                    {activeSection === 'assigned_by_me' && 'You haven\'t assigned any self-care tasks yet'}
                    {activeSection === 'completed' && 'No completed self-care tasks yet'}
                  </p>
                  {activeSection !== 'completed' && (
                    <button 
                      onClick={() => navigate('/dashboard/tasks/new?dimension=self_care')} 
                      className="gradient-btn"
                    >
                      Create Task
                    </button>
                  )}
                </div>
              ) : (
                filterTasks(taskData[activeSection]).map(renderTaskCard)
              )}
            </div>
          )}
        </>
      )}

      {/* Explore Resources Tab Content */}
      {activeTab === 'explore' && (
        <>
          {/* Info Banner */}
          <div style={{
            padding: '16px 20px',
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(52, 211, 153, 0.1))',
            borderRadius: '12px',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <BookOpen size={24} style={{ color: '#10B981' }} />
            <div>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#059669' }}>
                Wellness Resources
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Explore courses and resources uploaded by educators to support your well-being journey
              </p>
            </div>
          </div>

          {/* Search */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ position: 'relative', maxWidth: '400px' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)'
              }} />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)'
                }}
              />
            </div>
          </div>

          {/* Programs Grid */}
          {loadingPrograms ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <div className="loading-spinner"></div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '20px'
            }}>
              {programs.filter(p => p.status === 'published').length === 0 ? (
                <div className="card" style={{ padding: '60px', textAlign: 'center', gridColumn: '1 / -1' }}>
                  <BookOpen size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
                  <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No resources available yet</h3>
                  <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>
                    Wellness resources from educators will appear here once published
                  </p>
                </div>
              ) : (
                programs
                  .filter(p => p.status === 'published')
                  .filter(p => {
                    if (!searchQuery) return true;
                    const query = searchQuery.toLowerCase();
                    return p.title?.toLowerCase().includes(query) ||
                           p.description?.toLowerCase().includes(query);
                  })
                  .map(renderProgramCard)
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SelfCare;
